"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Vault = void 0;
const tslib_1 = require("tslib");
const quarry_sdk_1 = require("@quarryprotocol/quarry-sdk");
const solana_contrib_1 = require("@saberhq/solana-contrib");
const token_utils_1 = require("@saberhq/token-utils");
const web3_js_1 = require("@solana/web3.js");
class Vault {
    constructor(quarryPool, pool, vault) {
        this.quarryPool = quarryPool;
        this.pool = pool;
        this.vault = vault;
    }
    get provider() {
        return this.quarryPool.provider;
    }
    get program() {
        return this.quarryPool.programs.SunnyPoolQuarry;
    }
    /**
     * Deposit LP tokens into a vault.
     * @param amount
     * @returns
     */
    deposit(amount) {
        return (0, tslib_1.__awaiter)(this, void 0, void 0, function* () {
            const owner = this.provider.wallet.publicKey;
            const { address: ata, instruction } = yield (0, token_utils_1.getOrCreateATA)({
                provider: this.provider,
                mint: this.pool.data.vendorMint,
                owner,
            });
            if (instruction) {
                throw new Error("User has no tokens to deposit");
            }
            const vaultVendorTokenAccount = yield (0, token_utils_1.getATAAddress)({
                mint: this.pool.data.vendorMint,
                owner: this.vault.key,
            });
            return new solana_contrib_1.TransactionEnvelope(this.quarryPool.provider, [
                token_utils_1.SPLToken.createTransferInstruction(token_utils_1.TOKEN_PROGRAM_ID, ata, vaultVendorTokenAccount, owner, [], amount.toU64()),
            ])
                .combine(yield this.depositVendor())
                .combine(yield this.stakeInternal());
        });
    }
    /**
     * Deposits tokens into the vendor.
     * (Not recommended-- you probably want {@link Vault#deposit}.)
     * @returns
     */
    depositVendor() {
        return (0, tslib_1.__awaiter)(this, void 0, void 0, function* () {
            const stake = yield this.getVendorStakeAccounts();
            const vendorMint = this.pool.data.vendorMint;
            const vaultVendorTokenAccount = yield (0, token_utils_1.getATAAddress)({
                mint: vendorMint,
                owner: this.vault.key,
            });
            return new solana_contrib_1.TransactionEnvelope(this.provider, [
                this.program.instruction.depositVendor({
                    accounts: {
                        vaultOwner: this.provider.wallet.publicKey,
                        vaultVendorTokenAccount,
                        stake,
                    },
                }),
            ]);
        });
    }
    /**
     * Stakes internal tokens into the quarry.
     * (Not recommended-- you probably want {@link Vault#deposit}.)
     * @returns
     */
    stakeInternal() {
        return (0, tslib_1.__awaiter)(this, void 0, void 0, function* () {
            const stake = yield this.getInternalStakeAccounts();
            const internalMint = this.pool.data.internalMint;
            const internalMintTokenAccount = yield (0, token_utils_1.getATAAddress)({
                mint: internalMint,
                owner: this.vault.key,
            });
            return new solana_contrib_1.TransactionEnvelope(this.provider, [
                this.program.instruction.stakeInternal({
                    accounts: {
                        vaultOwner: this.provider.wallet.publicKey,
                        internalMint,
                        internalMintTokenAccount,
                        stake,
                    },
                }),
            ]);
        });
    }
    /**
     * Withdraw LP tokens from a vault.
     * @param amount
     * @returns
     */
    withdraw(amount) {
        return (0, tslib_1.__awaiter)(this, void 0, void 0, function* () {
            const unstake = yield this.unstakeInternal(amount);
            const withdrawVendor = yield this.withdrawVendor(amount);
            const withdrawVendorFromVault = yield this.withdrawVendorFromVault();
            return solana_contrib_1.TransactionEnvelope.combineAll(unstake, withdrawVendor, withdrawVendorFromVault);
        });
    }
    /**
     * Unstakes internal tokens from the quarry.
     * (Not recommended-- you probably want {@link Vault#withdraw}.)
     * @returns
     */
    unstakeInternal(amount) {
        return (0, tslib_1.__awaiter)(this, void 0, void 0, function* () {
            const stake = yield this.getInternalStakeAccounts();
            const internalMint = this.pool.data.internalMint;
            const internalMintTokenAccount = yield (0, token_utils_1.getATAAddress)({
                mint: internalMint,
                owner: this.vault.key,
            });
            return new solana_contrib_1.TransactionEnvelope(this.provider, [
                this.program.instruction.unstakeInternal(amount.toU64(), {
                    accounts: {
                        vaultOwner: this.provider.wallet.publicKey,
                        internalMint,
                        internalMintTokenAccount,
                        stake,
                    },
                }),
            ]);
        });
    }
    /**
     * Withdraws vendor tokens from the quarry.
     * (Not recommended-- you probably want {@link Vault#withdraw}.)
     * @returns
     */
    withdrawVendor(amount) {
        return (0, tslib_1.__awaiter)(this, void 0, void 0, function* () {
            const stake = yield this.getVendorStakeAccounts();
            const vendorMint = this.pool.data.vendorMint;
            const vaultVendorTokenAccount = yield (0, token_utils_1.getATAAddress)({
                mint: vendorMint,
                owner: this.vault.key,
            });
            return new solana_contrib_1.TransactionEnvelope(this.provider, [
                this.program.instruction.withdrawVendor(amount.toU64(), {
                    accounts: {
                        vaultOwner: this.provider.wallet.publicKey,
                        vaultVendorTokenAccount,
                        stake,
                    },
                }),
            ]);
        });
    }
    /**
     * Withdraws unstaked vendor tokens from the vault.
     * (Not recommended-- you probably want {@link Vault#withdraw}.)
     * @returns
     */
    withdrawVendorFromVault() {
        return (0, tslib_1.__awaiter)(this, void 0, void 0, function* () {
            const owner = this.provider.wallet.publicKey;
            const vaultVendorAccount = yield (0, token_utils_1.getATAAddress)({
                mint: this.pool.data.vendorMint,
                owner: this.vault.key,
            });
            const ownerVendorATA = yield (0, token_utils_1.getOrCreateATA)({
                provider: this.provider,
                mint: this.pool.data.vendorMint,
                owner,
            });
            const feeATA = yield (0, token_utils_1.getATAAddress)({
                mint: this.pool.data.vendorMint,
                owner: this.pool.key,
            });
            const withdrawVendorFromVaultIx = this.program.instruction.withdrawFromVault({
                accounts: {
                    owner,
                    pool: this.pool.key,
                    vault: this.vault.key,
                    vaultTokenAccount: vaultVendorAccount,
                    tokenDestination: ownerVendorATA.address,
                    feeDestination: feeATA,
                    tokenProgram: token_utils_1.TOKEN_PROGRAM_ID,
                },
            });
            return new solana_contrib_1.TransactionEnvelope(this.provider, [
                ...(ownerVendorATA.instruction ? [ownerVendorATA.instruction] : []),
                withdrawVendorFromVaultIx,
            ]);
        });
    }
    withdrawFromVault(withdrawMint) {
        return (0, tslib_1.__awaiter)(this, void 0, void 0, function* () {
            const owner = this.provider.wallet.publicKey;
            const vaultVendorAccount = yield (0, token_utils_1.getATAAddress)({
                mint: withdrawMint,
                owner: this.vault.key,
            });
            const ownerVendorATA = yield (0, token_utils_1.getATAAddress)({
                mint: withdrawMint,
                owner,
            });
            const feeATA = yield (0, token_utils_1.getATAAddress)({
                mint: withdrawMint,
                owner: this.pool.key,
            });
            const withdrawVendorFromVaultIx = this.program.instruction.withdrawFromVault({
                accounts: {
                    owner,
                    pool: this.pool.key,
                    vault: this.vault.key,
                    vaultTokenAccount: vaultVendorAccount,
                    tokenDestination: ownerVendorATA,
                    feeDestination: feeATA,
                    tokenProgram: token_utils_1.TOKEN_PROGRAM_ID,
                },
            });
            return new solana_contrib_1.TransactionEnvelope(this.provider, [withdrawVendorFromVaultIx]);
        });
    }
    claimInternalRewards() {
        return (0, tslib_1.__awaiter)(this, void 0, void 0, function* () {
            return yield this.claimRewardsCommon(this.pool.data.internalMint, yield this.getInternalStakeAccounts());
        });
    }
    claimVendorRewards() {
        return (0, tslib_1.__awaiter)(this, void 0, void 0, function* () {
            return yield this.claimRewardsCommon(this.pool.data.vendorMint, yield this.getVendorStakeAccounts());
        });
    }
    /**
     * Claims internal mining rewards.
     * @param amount
     * @returns
     */
    claimRewardsCommon(quarryMint, stake, vaultOwner = this.provider.wallet.publicKey) {
        return (0, tslib_1.__awaiter)(this, void 0, void 0, function* () {
            const rewarderKey = stake.rewarder;
            const rewarder = yield this.quarryPool.sdk.quarrySDK.programs.Mine.account.rewarder.fetch(rewarderKey);
            const [minter] = yield (0, quarry_sdk_1.findMinterAddress)(rewarder.mintWrapper, rewarderKey, this.quarryPool.sdk.quarrySDK.programs.MintWrapper.programId);
            const vault = this.vault.key;
            const withdrawMint = rewarder.rewardsTokenMint;
            const vaultATAs = yield (0, token_utils_1.getOrCreateATAs)({
                provider: this.provider,
                mints: {
                    quarry: quarryMint,
                    rewards: withdrawMint,
                },
                owner: vault,
            });
            const ownerATAs = yield (0, token_utils_1.getOrCreateATAs)({
                provider: this.provider,
                mints: {
                    rewards: withdrawMint,
                },
                owner: vaultOwner,
            });
            const feeATA = yield (0, token_utils_1.getOrCreateATA)({
                provider: this.provider,
                mint: withdrawMint,
                owner: this.pool.key,
            });
            return new solana_contrib_1.TransactionEnvelope(this.provider, [
                ...vaultATAs.instructions,
                ...ownerATAs.instructions,
                ...(feeATA.instruction ? [feeATA.instruction] : []),
                this.program.instruction.claimRewards({
                    accounts: {
                        mintWrapper: rewarder.mintWrapper,
                        mintWrapperProgram: this.quarryPool.sdk.quarrySDK.programs.MintWrapper.programId,
                        minter,
                        rewardsTokenMint: withdrawMint,
                        rewardsTokenAccount: vaultATAs.accounts.rewards,
                        claimFeeTokenAccount: rewarder.claimFeeTokenAccount,
                        stakeTokenAccount: vaultATAs.accounts.quarry,
                        stake,
                    },
                }),
            ]).combine(yield this.withdrawFromVault(withdrawMint));
        });
    }
    getInternalStakeAccounts() {
        return (0, tslib_1.__awaiter)(this, void 0, void 0, function* () {
            const { internalMint } = this.pool.data;
            const rewarder = this.quarryPool.sdk.environment.rewarder;
            const [quarry] = yield (0, quarry_sdk_1.findQuarryAddress)(rewarder, internalMint);
            const [miner] = yield (0, quarry_sdk_1.findMinerAddress)(quarry, this.vault.key);
            const minerVault = yield (0, token_utils_1.getATAAddress)({
                mint: internalMint,
                owner: miner,
            });
            return Object.assign(Object.assign({}, this.commonStakeAccounts), { rewarder,
                quarry,
                miner,
                minerVault });
        });
    }
    getVendorStakeAccounts() {
        return (0, tslib_1.__awaiter)(this, void 0, void 0, function* () {
            const rewarder = this.pool.data.rewarder;
            const { vendorMint } = this.pool.data;
            const [quarry] = yield (0, quarry_sdk_1.findQuarryAddress)(rewarder, vendorMint);
            const [miner] = yield (0, quarry_sdk_1.findMinerAddress)(quarry, this.vault.key);
            const minerVault = yield (0, token_utils_1.getATAAddress)({
                mint: vendorMint,
                owner: miner,
            });
            return Object.assign(Object.assign({}, this.commonStakeAccounts), { rewarder,
                quarry,
                miner,
                minerVault });
        });
    }
    get commonStakeAccounts() {
        return {
            pool: this.vault.data.pool,
            vault: this.vault.key,
            tokenProgram: token_utils_1.TOKEN_PROGRAM_ID,
            mineProgram: this.quarryPool.sdk.quarrySDK.mine.program.programId,
            clock: web3_js_1.SYSVAR_CLOCK_PUBKEY,
        };
    }
}
exports.Vault = Vault;
//# sourceMappingURL=vault.js.map